// CONTRACT:C4-DESIGN-TOKENS.1.0
// ContributionAnchor — Item 6, the DORMANT safety-of-life slot (0.1b).
// Spec: docs/ux/SHELL-COMPOSITION.md §2.4, §5 A4.
//
// THE SLOT, NOT THE LOOP. 0.1b ships ONLY the reserved, inert anchor at the
// bottom edge of the left rail. The WRITE / break-through (broadcast) loop is
// milestone-deferred to 0.3b. So this component:
//   - is present but DIM / dormant at rest (present-but-recessive),
//   - on press shows a small "deferred — DDIL data plane (0.3b)" hint and NOTHING
//     else: it NEVER broadcasts, never opens a write surface,
//   - reads NOTHING live (reserved). No PDP-derived count, no denominator — there
//     is nothing here for the Glomar invariant to leak.
//
// CLEAN-LAW: deferred-slot discipline (nothing lands homeless; the safety-of-life
// home is reserved NOW so 0.3b drops the loop into a place that already exists).
//
// HEAVY-PANEL LAW (R-LAW-3): the deferred hint is a LIGHT surface — a small
// dismissable note that displaces nothing — so it does NOT call `claimHeavy`.
// It is dismissable, so it registers with `useEscapeStack` (Esc pops the hint).

import { useState } from "react";
import { useEscapeStack } from "./useEscapeStack.ts";
import "./anchor.css";

/** The dormant anchor takes the standard `{ status, handle }` orchestrator props
 *  for a uniform mount, but READS NOTHING from either in 0.1b — the slot is
 *  reserved. To keep the GROUP-A invariant "zero host / MapLibre pulled in", the
 *  props are typed STRUCTURALLY (not against `CopStatus`/`CopHandle` from cop.ts),
 *  since this component dereferences neither. (0.3b will reach
 *  `handle.breakThrough()` from here — wired by the orchestrator at that point.)
 *  Both are optional so the slot is trivially mountable / testable in isolation. */
export interface ContributionAnchorProps {
  status?: unknown;
  handle?: unknown;
  /** Render form. "gutter" = the original dashed full-width slot. "rail" = the 54px
   *  FUNCTION RAIL form: a compact 40×40 inert icon (no word-label) — the deferred
   *  slot is PRESERVED (§2.4, never dropped), just resized to the rail. Behavior
   *  (the deferred hint on press, Esc pop) is unchanged. */
  variant?: "gutter" | "rail";
}

export function ContributionAnchor({ variant = "gutter" }: ContributionAnchorProps): JSX.Element {
  // The only state in 0.1b: whether the deferred-affordance hint is showing.
  // This is purely local UI; it touches no host state and broadcasts nothing.
  const [hintOpen, setHintOpen] = useState(false);

  // Esc pops the hint (one level). Light surface — not on the heavy-panel slot.
  useEscapeStack(() => setHintOpen(false), hintOpen);

  const rail = variant === "rail";

  return (
    <div className="anchor" data-deferred="0.3b" data-variant={variant}>
      <button
        type="button"
        className="anchor__btn"
        // It is present but INERT: `aria-disabled` (not `disabled`, so it stays
        // focusable/pressable to reveal the hint) signals there is no live action.
        aria-disabled="true"
        aria-haspopup="dialog"
        aria-expanded={hintOpen}
        aria-label="Contribute (safety-of-life broadcast) — deferred to 0.3b"
        title="Contribute — deferred to 0.3b"
        onClick={() => setHintOpen((v) => !v)}
      >
        {/* Shape-primary mark: an upward broadcast caret. Calm, monochrome,
            no affiliation color, no motion. Decorative — labelled via aria. */}
        <svg
          className="anchor__mark"
          viewBox="0 0 24 24"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M12 5 L19 18 L12 14.5 L5 18 Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        </svg>
        {/* The rail is icon-only — no word-label (the words live in title/aria). */}
        {!rail && <span className="anchor__label">Contribute</span>}
      </button>

      {hintOpen && (
        <div
          className="anchor__hint"
          role="status"
          aria-live="polite"
          // The hint dismisses on click anywhere on it (and on Esc).
          onClick={() => setHintOpen(false)}
        >
          <span className="anchor__hint-tag">DEFERRED · 0.3b</span>
          <span className="anchor__hint-text">
            Safety-of-life contribution arrives with the DDIL data plane. This slot
            is reserved — it does not broadcast yet.
          </span>
        </div>
      )}
    </div>
  );
}
